import '../../tools-pages.css'

import { nanoid } from 'nanoid'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ChatMessages, ConfirmModal, Spinner } from '../../../Components'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { getStateDefaultData } from '../../Gigi/GigiForm/Functions'
import {
  Campi1a10Section,
  HotelSection,
  PlaneSection,
  TrainSection,
  TransferSection,
  TravellerMidOfficeSection
} from '../../Gigi/GigiForm/Sections'
import { getDiffObj, useDefaultChatData } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

export const AutoTest = () => {
  const { autoTestId } = useParams()
  const { data: autoTest, isSuccess, isError } = useCQuery(['autoTest', parseInt(autoTestId)])
  const { data: autoTests = [] } = useCQuery(['autoTests'])
  const { correctState } = autoTest || {}
  const defaultProps = [correctState, () => {}]
  const { isSuccess: isSuccessDataChat, ...data } = useDefaultChatData()
  const navigate = useNavigate()
  const [modalVisible, setModalVisible] = useState(false)

  useEffect(() => {
    if (isError) {
      showToast('error', t.errore, t.mcTestNonTrovato)
      navigate('/autotest/list')
    }
  }, [isError])

  const runTest = () =>
    FC.service('public').create({
      ...data,
      userInput: autoTest?.messages?.[0]?.message,
      autoTestId: parseInt(autoTestId),
      idConvFiera: nanoid()
    })

  const validateTest = async () => {
    try {
      const { ok, message } = await FC.service('info').create({ action: 'validateTest', autoTestId })
      invalidateQuery(['autoTest', parseInt(autoTestId)])
      if (ok) return showToast('success', t.success, t.mcAutotestAggiornato)
      showToast('error', t.errore, message)
    } catch (error) {
      showToast('error', t.errore, error?.message || 'Errore durante la validazione del test')
    }
  }

  if (!isSuccessDataChat || !isSuccess) return <Spinner />
  return (
    <div className="autotest-page" style={{ maxWidth: 1500 }}>
      <ConfirmModal
        visible={[modalVisible, setModalVisible]}
        header={t.conferma}
        onConfirm={validateTest}
        label={t.mxValidaTestLabel}
      />

      <div className="nora-page__head">
        <div>
          <h1 className="nora-page__title">
            {t.mxTestIdPrefix}
            {parseInt(autoTestId)}
          </h1>
          <p className="nora-page__sub">{t.mxValidazioneEstrazione}</p>
        </div>
        <div className="nora-page__actions">
          <button type="button" className="nbtn" onClick={() => navigate('/autotest/list')}>
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.tornaIndietro}
          </button>
          <button
            type="button"
            className="nbtn"
            disabled={!autoTests.find((test) => test.id === parseInt(autoTestId) - 1)}
            onClick={() => navigate(`/autotest/${parseInt(autoTestId) - 1}`)}
          >
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.mxTestPrecedente}
          </button>
          <button
            type="button"
            className="nbtn"
            disabled={!autoTests.find((test) => test.id === parseInt(autoTestId) + 1)}
            onClick={() => navigate(`/autotest/${parseInt(autoTestId) + 1}`)}
          >
            {t.mxTestSuccessivo}
            <i className="pi pi-chevron-right" style={{ fontSize: 14 }} />
          </button>
          <button type="button" className="nbtn" onClick={runTest}>
            <i className="pi pi-play" style={{ fontSize: 14 }} />
            {t.mxEseguiTest}
          </button>
          <button
            type="button"
            className="nbtn nbtn--primary"
            onClick={() => setModalVisible(true)}
          >
            <i className="pi pi-check" style={{ fontSize: 14 }} />
            {t.mxValidaTest}
          </button>
        </div>
      </div>

      <div className="ncard autotest-chat-card" style={{ marginBottom: 18 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-comments" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.mxConversazioneTest}</h3>
            <div className="ncard__sub">
              {autoTest?.messages?.length || 0}
              {t.mxMessaggiSuffix}
            </div>
          </div>
        </div>
        <div className="ncard__body">
          <ChatMessages messages={autoTest?.messages} onVisualized={false} />
        </div>
      </div>

      <div className="autotest-compare-grid">
        <div>
          <div className="autotest-compare-col__title">
            <i className="pi pi-check-circle" style={{ fontSize: 16 }} />
            {t.mxRisultatoAtteso}
          </div>
          <TravellerMidOfficeSection state={defaultProps} readOnly />
          <TransferSection state={defaultProps} readOnly />
          <Campi1a10Section state={defaultProps} readOnly />
          <PlaneSection state={defaultProps} readOnly />
          <TrainSection state={defaultProps} readOnly />
          <HotelSection state={defaultProps} readOnly />
        </div>
        <div>
          <div className="autotest-compare-col__title">
            <i className="pi pi-bolt" style={{ fontSize: 16 }} />
            {t.mxRisultatoOttenuto}
          </div>
          <TestResult conversationId={autoTest?.conversationId} correctState={correctState} />
        </div>
      </div>
    </div>
  )
}

const TestResult = ({ conversationId = null, correctState = {} }) => {
  const {
    data: conversation = {},
    isSuccess,
    isError
  } = useCQuery(['conversation', conversationId])
  const [lastProcessStepId, setLastProcessStepId] = useState()
  const [state, setState] = useState()
  const [diff, setDiff] = useState({})
  const [lastStatus, setLastStatus] = useState('')

  useEffect(() => {
    if (isSuccess) {
      const lastProcessStep = conversation?.lastProcessStep
      const lastPayload = lastProcessStep?.initialState || {}
      const lastProcess = conversation?.processes[conversation?.processes.length - 1]
      const lastStatus = lastProcess?.state
      setLastStatus(lastStatus)

      if (
        !state ||
        lastProcessStepId !== lastProcessStep?.id ||
        lastProcessStep?.initialState?.forceUpdate
      ) {
        setLastProcessStepId(lastProcessStep?.id)
        const newState = getStateDefaultData(lastPayload)
        setDiff(getDiffObj(correctState, newState))
        setState(getStateDefaultData(lastPayload))
      }
    }
  }, [isSuccess, conversation])

  if (isError || !conversationId) return null
  if (!isSuccess) return <Spinner />

  const defaultProps = [state, () => {}]
  const diffCount =
    (diff?.traveller?.length || 0) +
    (diff?.transfer?.length || 0) +
    (diff?.plane?.length || 0) +
    (diff?.train?.length || 0) +
    (diff?.hotel?.length || 0)

  return (
    <div>
      {lastStatus === 2 && (
        <span
          className="nora-status nora-status--running"
          style={{ marginBottom: 12, display: 'inline-flex' }}
        >
          <span className="nora-status__dot" />
          <span className="nora-status__label">{t.mxInEsecuzione}</span>
        </span>
      )}
      {diffCount > 0 && (
        <span
          className="nbadge nbadge--warn"
          style={{ marginBottom: 12, display: 'inline-flex', marginLeft: 8 }}
        >
          <i className="pi pi-exclamation-triangle" style={{ fontSize: 11, marginRight: 4 }} />
          {diffCount} diff
        </span>
      )}
      <TravellerMidOfficeSection state={defaultProps} readOnly diff={diff?.traveller || []} />
      <TransferSection state={defaultProps} readOnly diff={diff?.transfer || []} />
      <Campi1a10Section state={defaultProps} readOnly />
      <PlaneSection state={defaultProps} readOnly diff={diff?.plane || []} />
      <TrainSection state={defaultProps} readOnly diff={diff?.train || []} />
      <HotelSection state={defaultProps} readOnly diff={diff?.hotel || []} />
    </div>
  )
}
